// Mi Inicio: el único portal del empleado (lote C, 07-oct-2026).
// Pestañas: Hoy, Vacaciones, Nóminas, Documentos, Registro horario y Mis
// obligaciones; la activa va en `?tab=`. Lee todo de GET /portal/mi-inicio
// (si falla, la pestaña Hoy cae a GET /portal/mi-dashboard). Móvil primero.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { portalAPI, gestionDocumentalAPI, controlHorarioAPI, textoErrorApi } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { useVistaEmpleado, AVISO_SIMULACION } from '../context/VistaEmpleadoContext';
import { fechaLocalISO, lunesDeLaSemana } from '../utils/pantallasUnDia';
import { hoyMadrid } from '../utils/fechas';
import { urlConToken } from '../utils/urlConToken';
import VisorDocumento from '../components/VisorDocumento';
import FichajeRapido, { estadoFichaje } from '../components/empleados/FichajeRapido';
import PestanaFirmas from '../components/empleados/PestanaFirmas';
import { useGuia, EVENTO_GUIA_TERMINADA } from '../context/GuiaContext';
import { pasosBienvenida } from '../components/guia/guionBienvenida';
import RegistroHorarioEmpleado, {
  useAvisoTemporal, AvisoInline, DialogoConfirmar,
} from '../components/empleados/RegistroHorarioEmpleado';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Clock, Calendar, FileText, Bell, CheckCircle2, MapPin, Download, User, ChevronRight,
  RefreshCw, AlertCircle, Eye, CheckSquare, Plane, Wallet, ListChecks, Plus, FileCheck, Compass, Landmark,
} from 'lucide-react';

// ==================== UTILIDADES ====================

const ROLES_LEGIBLES = {
  direccion: 'Dirección',
  operaciones: 'Operaciones',
  comercial: 'Comercial',
  finanzas: 'Finanzas',
  rrhh: 'RRHH',
  encargado: 'Encargado',
  admin: 'Administración',
  invitado: 'Invitado',
};

const rolLegible = (rol) => {
  if (!rol) return '';
  const clave = String(rol).toLowerCase();
  if (ROLES_LEGIBLES[clave]) return ROLES_LEGIBLES[clave];
  return clave.charAt(0).toUpperCase() + clave.slice(1);
};

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];

const TIPOS_DOCUMENTO = {
  contrato: 'Contratos',
  nomina: 'Nóminas',
  certificado: 'Certificados',
  formacion: 'Formación',
  dni: 'DNI',
  seguridad_social: 'Seguridad Social',
  otro: 'Otros',
};

const TIPOS_SOLICITUD = [
  { valor: 'vacaciones', texto: 'Vacaciones' },
  { valor: 'asuntos_propios', texto: 'Asuntos propios' },
  { valor: 'permiso', texto: 'Permiso' },
  { valor: 'baja_medica', texto: 'Baja médica' },
];

const ESTADOS_SOLICITUD = {
  pendiente: { texto: 'Pendiente', clases: 'bg-yellow-100 text-yellow-800' },
  aprobada: { texto: 'Aprobada', clases: 'bg-green-100 text-green-800' },
  rechazada: { texto: 'Rechazada', clases: 'bg-red-100 text-red-800' },
};

const PESTANAS = [
  { clave: 'hoy', texto: 'Hoy', Icono: Clock },
  { clave: 'vacaciones', texto: 'Vacaciones', Icono: Plane },
  { clave: 'nominas', texto: 'Nóminas', Icono: Wallet },
  { clave: 'documentos', texto: 'Documentos', Icono: FileText },
  { clave: 'registro', texto: 'Registro horario', corto: 'Registro', Icono: Calendar },
  { clave: 'obligaciones', texto: 'Mis obligaciones', corto: 'Obligaciones', Icono: ListChecks },
];
const CLAVES_PESTANAS = PESTANAS.map((p) => p.clave);

// ==================== IBAN ====================
// Misma regla que el servidor (validadores_empleado.py: iban_valido y aviso_iban)

const limpiarIban = (valor) => String(valor || '').replace(/[\s\-./]/g, '').toUpperCase();

// "ES9121000418..." -> "ES91 2100 0418 ..." (bloques de 4 al escribir)
const formatearIban = (valor) => {
  const limpio = limpiarIban(valor).replace(/[^A-Z0-9]/g, '').slice(0, 34);
  return limpio.replace(/(.{4})/g, '$1 ').trim();
};

// Módulo 97 por trozos (sin BigInt)
const ibanCuadra = (limpio) => {
  const reordenado = limpio.slice(4) + limpio.slice(0, 4);
  let resto = 0;
  for (const c of reordenado) {
    const v = parseInt(c, 36);
    if (Number.isNaN(v)) return false;
    resto = Number(`${resto}${v}`) % 97;
  }
  return resto === 1;
};

// Texto del error o null si el IBAN es válido
const errorIban = (valor) => {
  const v = limpiarIban(valor);
  if (!v) return 'Escribe tu IBAN.';
  if (v.startsWith('ES')) {
    if (!/^ES\d{22}$/.test(v)) {
      return `El IBAN español tiene 24 caracteres: ES y 22 números (llevas ${v.length}).`;
    }
  } else if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(v)) {
    return 'El IBAN no tiene un formato válido: empieza por ES y tiene 24 caracteres.';
  }
  if (!ibanCuadra(v)) return 'Los dígitos de control no cuadran: revisa que lo hayas copiado bien.';
  return null;
};

const ibanPendiente = (obligaciones) => (obligaciones?.items || [])
  .some((it) => it.accion_empleado === 'iban' && it.ok === false);

// "09:30:00" -> "09:30"
const horaCorta = (hora) => (hora ? String(hora).slice(0, 5) : '');

// Fecha corta; las fechas "YYYY-MM-DD" se pintan sin pasar por UTC
const fechaCorta = (valor) => {
  if (!valor) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor));
  if (m) return `${Number(m[3])}/${Number(m[2])}/${m[1]}`;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES');
};

const fechaEvento = (fecha) => {
  if (!fecha) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(fecha));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

const numero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatoNumero = (v) => (numero(v) ?? 0).toLocaleString('es-ES', { maximumFractionDigits: 1 });

// Resumen de horas tolerante: `horas_mes` decimal (nuevo) u objeto
// {total_horas, total_minutos, dias_trabajados} (antiguo)
const resumenHoras = (datos) => {
  const hm = datos?.horas_mes;
  let horasMes = 0;
  let diasMes = numero(datos?.dias_trabajados_mes);
  if (hm && typeof hm === 'object') {
    horasMes = (numero(hm.total_horas) || 0) + (numero(hm.total_minutos) || 0) / 60;
    if (diasMes == null) diasMes = numero(hm.dias_trabajados);
  } else if (numero(hm) != null) {
    horasMes = numero(hm);
  } else if (numero(datos?.minutos_mes) != null) {
    horasMes = numero(datos.minutos_mes) / 60;
  }
  let horasSemana = numero(datos?.horas_semana);
  if (horasSemana == null && numero(datos?.minutos_semana) != null) {
    horasSemana = numero(datos.minutos_semana) / 60;
  }
  return { horasMes, diasMes: diasMes || 0, horasSemana: horasSemana || 0 };
};

const claveEvento = (ev) => `${ev.id || ev.numero}-${ev.jornada_id || ev.jornada_etiqueta || 'unico'}-${ev.fecha_evento || ev.fecha || ''}`;

// Días naturales entre dos fechas "YYYY-MM-DD", ambas incluidas (sin UTC ni horario de verano)
const diasEntre = (inicio, fin) => {
  const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(inicio || '');
  const b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fin || '');
  if (!a || !b) return 0;
  const ta = Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
  const tb = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
  return Math.floor((tb - ta) / 86400000) + 1;
};

// Mes y año de una nómina: `periodo` "YYYY-MM" (servidor) o `mes`/`anio`
const periodoNomina = (n) => {
  const m = /^(\d{4})-(\d{2})/.exec(String(n.periodo || ''));
  if (m) return { anio: Number(m[1]), mes: Number(m[2]) };
  return { anio: numero(n.anio), mes: numero(n.mes) };
};

const nombreNomina = (n) => {
  const { mes, anio } = periodoNomina(n);
  if (mes && mes >= 1 && mes <= 12 && anio) return `Nómina de ${MESES[mes - 1]} ${anio}`;
  return n.nombre || 'Nómina';
};

const claveOrdenNomina = (n) => {
  const { mes, anio } = periodoNomina(n);
  if (mes && anio) return `${anio}-${String(mes).padStart(2, '0')}`;
  return String(n.fecha_documento || '');
};

// Estado del fichaje de hoy: `hoy.fichaje` del servidor nuevo o, si falta,
// el registro de hoy (abierto o el último cerrado) leído aparte
const ESTADOS_FICHAJE = ['sin_fichar', 'trabajando', 'en_pausa', 'fuera_de_jornada', 'terminado'];

// Documentos por tipo como lista [{tipo, etiqueta, documentos}]: el servidor
// manda ya la lista; se acepta también un objeto {tipo: [docs]}
const listaPorTipo = (porTipo) => {
  if (Array.isArray(porTipo)) {
    return porTipo.map((g) => ({
      tipo: g.tipo || 'otro',
      etiqueta: g.etiqueta || TIPOS_DOCUMENTO[g.tipo] || g.tipo || 'Otros',
      documentos: g.documentos || [],
    }));
  }
  return Object.keys(porTipo || {}).map((tipo) => ({
    tipo, etiqueta: TIPOS_DOCUMENTO[tipo] || tipo, documentos: porTipo[tipo] || [],
  }));
};

// Saldo anidado en `saldo` (servidor) o plano; las solicitudes con `dias`
const normalizarVacaciones = (v) => {
  if (!v) return null;
  const saldo = v.saldo || v;
  return {
    anuales: saldo.anuales,
    devengados: saldo.devengados,
    devengados_fin_ano: saldo.devengados_fin_ano,
    usados: saldo.usados,
    pendientes_aprobacion: saldo.pendientes_aprobacion,
    disponibles: saldo.disponibles,
    solicitudes: (v.solicitudes || []).map((sol) => ({ ...sol, dias: sol.dias ?? sol.dias_solicitados })),
    proximas_ausencias: v.proximas_ausencias || [],
  };
};

// Normaliza la respuesta de mi-inicio (o de mi-dashboard como respaldo)
const normalizar = (data, parcial = false) => {
  const d = data || {};
  const hoy = d.hoy || d;
  let tareasLista = [];
  if (Array.isArray(d.tareas?.lista)) tareasLista = d.tareas.lista;
  else if (Array.isArray(d.tareas)) tareasLista = d.tareas;
  else if (Array.isArray(hoy.tareas_pendientes)) tareasLista = hoy.tareas_pendientes;
  else if (Array.isArray(d.tareas_pendientes)) tareasLista = d.tareas_pendientes;
  const totalTareas = numero(d.tareas?.total)
    ?? numero(hoy.total_tareas_pendientes)
    ?? numero(d.total_tareas_pendientes)
    ?? tareasLista.length;
  return {
    parcial,
    empleadoId: d.empleado_id || null,
    sinFicha: d.sin_ficha === true,
    nombre: d.empleado_nombre || '',
    rol: d.empleado_rol || '',
    hoy: {
      fichaje: hoy.fichaje || null,
      horas: resumenHoras(hoy),
      eventos: hoy.mis_eventos || [],
      calendario: hoy.calendario_semanal || [],
      tareas: tareasLista,
      totalTareas,
    },
    vacaciones: normalizarVacaciones(d.vacaciones),
    nominas: Array.isArray(d.nominas) ? d.nominas : (Array.isArray(d.nominas?.lista) ? d.nominas.lista : null),
    documentos: d.documentos
      ? { ...d.documentos, por_tipo: listaPorTipo(d.documentos.por_tipo), para_firmar: d.documentos.para_firmar || [] }
      : null,
    registroHorario: d.registro_horario || null,
    obligaciones: d.obligaciones || null,
    // Guía de bienvenida pendiente (la abre la página con GuiaInteractiva) y
    // pospuesta con "Saltar la guía" (ya no se abre sola; aviso para verla)
    tour_pendiente: d.tour_pendiente === true,
    tour_pospuesto: d.tour_pospuesto === true,
  };
};

// ==================== PIEZAS DE PRESENTACIÓN ====================

// `tour`: data-tour de la tarjeta (zona que resalta la guía de bienvenida)
const Tarjeta = ({ titulo, Icono, colorIcono = 'text-duetto-primary', extra, children, testId, tour }) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid={testId} data-tour={tour}>
    {titulo && (
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center min-w-0">
          {Icono && <Icono className={`w-5 h-5 mr-2 flex-shrink-0 ${colorIcono}`} />}
          <span className="truncate">{titulo}</span>
        </h2>
        {extra}
      </div>
    )}
    <div className="p-4 sm:p-6">{children}</div>
  </section>
);

const Vacio = ({ Icono = FileText, texto, subtexto }) => (
  <div className="text-center py-8 text-gray-400">
    <Icono className="w-10 h-10 mx-auto mb-2 opacity-50" />
    <p className="font-medium text-gray-500">{texto}</p>
    {subtexto && <p className="text-sm mt-1">{subtexto}</p>}
  </div>
);

const Cifra = ({ valor, texto, clases = 'bg-gray-50 border-gray-200 text-gray-800', testId }) => (
  <div className={`rounded-xl border p-3 text-center ${clases}`} data-testid={testId}>
    <div className="text-xl sm:text-2xl font-bold">{valor}</div>
    <div className="text-xs mt-0.5 opacity-80">{texto}</div>
  </div>
);

// Fila de documento con Ver (visor) y Descargar
const FilaDocumento = ({ titulo, subtitulo, url, onVer, testIdVer }) => (
  <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
    <div className="flex items-center min-w-0">
      <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
        <FileText className="w-5 h-5 text-purple-600" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{titulo}</div>
        {subtitulo && <div className="text-xs text-gray-500">{subtitulo}</div>}
      </div>
    </div>
    {url ? (
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={onVer}
          className="flex items-center gap-1 px-3 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          data-testid={testIdVer}
        >
          <Eye className="w-4 h-4" />
          Ver
        </button>
        <a
          href={urlConToken(url)}
          download
          target="_blank"
          rel="noopener noreferrer"
          title="Descargar"
          aria-label="Descargar"
          className="flex items-center gap-1 px-3 py-2.5 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Descargar</span>
        </a>
      </div>
    ) : (
      <span className="text-xs text-gray-400 flex-shrink-0">Sin archivo</span>
    )}
  </div>
);

// ==================== PESTAÑA HOY ====================

const CalendarioSemanal = ({ calendario }) => {
  const hoy = new Date();
  // El domingo es el último día de la semana; las fechas son locales, como el servidor
  const inicioSemana = lunesDeLaSemana(hoy);
  const dias = [];
  for (let i = 0; i < 7; i += 1) {
    const dia = new Date(inicioSemana);
    dia.setDate(inicioSemana.getDate() + i);
    dias.push(dia);
  }
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
        {dias.map((dia) => {
          const fechaStr = fechaLocalISO(dia);
          const eventosDelDia = calendario.filter((e) => e.fecha === fechaStr);
          const esHoy = dia.toDateString() === hoy.toDateString();
          return (
            <div
              key={fechaStr}
              className={`flex sm:block gap-3 sm:min-h-[130px] min-w-0 p-2 sm:p-3 rounded-xl border-2 ${
                esHoy ? 'bg-blue-50 border-blue-400' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className={`w-12 sm:w-auto flex-shrink-0 text-center sm:mb-2 ${esHoy ? 'text-blue-700' : 'text-gray-500'}`}>
                <div className="text-xs font-medium uppercase">
                  {dia.toLocaleDateString('es-ES', { weekday: 'short' })}
                </div>
                <div className={`text-xl sm:text-2xl font-bold ${esHoy ? 'text-blue-800' : 'text-gray-700'}`}>
                  {dia.getDate()}
                </div>
              </div>
              <div className="flex-1 min-w-0 space-y-1 self-center sm:self-auto">
                {eventosDelDia.length === 0 && (
                  <div className="text-xs text-gray-400 sm:hidden">Sin eventos</div>
                )}
                {/* Cada evento lleva al calendario en ese día */}
                {eventosDelDia.slice(0, 3).map((ev) => (
                  <Link
                    key={claveEvento(ev)}
                    to={`/calendario-eventos?fecha=${fechaStr}`}
                    className={`block text-xs p-1.5 rounded-lg line-clamp-2 sm:line-clamp-none sm:truncate hover:ring-2 hover:ring-blue-300 ${
                      ev.estoy_asignado ? 'bg-green-200 text-green-800 font-medium' : 'bg-gray-200 text-gray-700'
                    }`}
                    title={`${ev.cliente || ''}${ev.lugar ? ` · ${ev.lugar}` : ''}`}
                    data-testid={`inicio-semana-evento-${fechaStr}`}
                  >
                    {ev.hora && <span className="font-bold">{horaCorta(ev.hora)} </span>}
                    {ev.cliente}
                  </Link>
                ))}
                {eventosDelDia.length > 3 && (
                  <Link to={`/calendario-eventos?fecha=${fechaStr}`} className="inline-block py-1 text-xs text-gray-500 hover:underline">
                    +{eventosDelDia.length - 3} más
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-gray-500 justify-center">
        <span className="flex items-center"><span className="w-3 h-3 bg-green-200 rounded mr-2" />Estoy asignado</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-gray-200 rounded mr-2" />Otros eventos</span>
      </div>
    </>
  );
};

const PestanaHoy = ({ datos, fichajeHoy, onRecargar, onTarea, accionEnCurso, soloLectura = false, onAnadirIban }) => {
  const navigate = useNavigate();
  const { hoy, sinFicha, empleadoId } = datos;
  const faltaIban = ibanPendiente(datos.obligaciones);
  const fichaje = fichajeHoy || {};
  const estado = ESTADOS_FICHAJE.includes(fichaje.estado) ? fichaje.estado : 'sin_fichar';

  return (
    <div className="space-y-4 sm:space-y-6">
      {sinFicha ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="dashboard-empleado-sin-ficha">
          <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-gray-900">Tu usuario no tiene ficha de empleado</p>
            <p className="text-sm text-gray-600 mt-1">
              Pide a dirección que la enlace con tu email. Mientras tanto ves los eventos y el calendario.
            </p>
          </div>
        </div>
      ) : (
        <>
          <Tarjeta titulo="Fichar" Icono={Clock} testId="inicio-fichar">
            <FichajeRapido
              empleadoId={empleadoId}
              estado={estado}
              horaEntrada={fichaje.hora_entrada}
              horaSalida={fichaje.hora_salida}
              pausaDesde={fichaje.pausa_inicio || fichaje.pausa_desde}
              minutosHoy={fichaje.minutos_trabajados_hoy}
              permisos={typeof fichaje.puede_entrar === 'boolean' ? {
                entrar: fichaje.puede_entrar,
                pausar: !!fichaje.puede_pausar,
                reanudar: !!fichaje.puede_reanudar,
                salir: !!fichaje.puede_salir,
              } : null}
              onFichado={onRecargar}
              soloLectura={soloLectura}
            />
          </Tarjeta>

          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <Cifra valor={`${formatoNumero(hoy.horas.horasSemana)}h`} texto="Esta semana" clases="bg-green-50 border-green-200 text-green-800" testId="dashboard-empleado-horas-semana" />
            <Cifra valor={`${formatoNumero(hoy.horas.horasMes)}h`} texto="Este mes" clases="bg-blue-50 border-blue-200 text-blue-800" />
            <Cifra valor={hoy.horas.diasMes} texto="Días este mes" clases="bg-gray-50 border-gray-200 text-gray-800" />
          </div>

          <Tarjeta
            titulo="Tareas y avisos"
            Icono={Bell}
            colorIcono="text-orange-500"
            tour="tareas"
            extra={(
              <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0">
                {hoy.totalTareas} pendientes
              </span>
            )}
          >
            {faltaIban && (
              <div
                className="mb-3 p-3 sm:p-4 rounded-lg border-l-4 bg-amber-50 border-amber-500 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid="inicio-falta-iban"
              >
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <Landmark className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">Falta tu número de cuenta para la nómina</div>
                    <div className="text-sm text-gray-600 mt-0.5">Sin él no podemos pagarte la nómina por transferencia.</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onAnadirIban}
                  className="w-full sm:w-auto min-h-[48px] px-4 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700"
                  data-testid="inicio-falta-iban-anadir"
                >
                  Añadirlo
                </button>
              </div>
            )}
            {hoy.tareas.length > 0 ? (
              <div className="space-y-3">
                {hoy.tareas.map((tarea) => (
                  <div
                    key={tarea.id}
                    className={`p-3 sm:p-4 rounded-lg border-l-4 ${
                      tarea.prioridad === 'urgente' ? 'bg-red-50 border-red-500'
                        : tarea.prioridad === 'alta' ? 'bg-orange-50 border-orange-500'
                          : 'bg-blue-50 border-blue-500'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900">{tarea.titulo}</div>
                        <div className="text-sm text-gray-600 mt-1 break-words">{tarea.mensaje}</div>
                      </div>
                      {tarea.prioridad === 'urgente' && (
                        <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">Urgente</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-2 flex items-center gap-2">
                      <User className="w-3 h-3" />
                      {tarea.creado_por} · {fechaCorta(tarea.fecha)}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => onTarea(tarea.id, 'leer')}
                        disabled={tarea.leida || (!soloLectura && accionEnCurso !== null)}
                        aria-disabled={soloLectura || undefined}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-60${soloLectura ? ' opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {tarea.leida ? 'Leído' : (accionEnCurso === `${tarea.id}-leer` ? 'Marcando...' : 'Marcar leído')}
                      </button>
                      <button
                        type="button"
                        onClick={() => onTarea(tarea.id, 'realizada')}
                        disabled={!soloLectura && accionEnCurso !== null}
                        aria-disabled={soloLectura || undefined}
                        className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2.5 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 disabled:opacity-60${soloLectura ? ' opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <CheckSquare className="w-3.5 h-3.5" />
                        {accionEnCurso === `${tarea.id}-realizada` ? 'Marcando...' : 'Realizado'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (!faltaIban && (
              <Vacio Icono={CheckCircle2} texto="No tienes tareas pendientes" />
            ))}
          </Tarjeta>
        </>
      )}

      <Tarjeta
        titulo="Próximos eventos (7 días)"
        Icono={Calendar}
        colorIcono="text-blue-600"
        extra={<span className="text-sm text-gray-500 flex-shrink-0">{hoy.eventos.length}</span>}
      >
        {hoy.eventos.length > 0 ? (
          <div className="space-y-3">
            {hoy.eventos.map((evento) => (
              <div
                key={claveEvento(evento)}
                className={`p-3 sm:p-4 rounded-xl border-2 ${
                  evento.estoy_asignado ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{evento.cliente}</div>
                    {evento.jornada_etiqueta && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                        {evento.jornada_etiqueta}
                      </span>
                    )}
                    {evento.lugar_evento && (
                      <div className="flex items-center text-gray-500 text-sm mt-1 min-w-0">
                        <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{evento.lugar_evento}</span>
                      </div>
                    )}
                    {evento.estoy_asignado && (
                      <span className="inline-flex items-center mt-2 px-2.5 py-1 bg-green-600 text-white text-xs font-medium rounded-full">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {evento.mi_rol ? `Asignado: ${evento.mi_rol}` : 'Asignado'}
                      </span>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-blue-600">{fechaEvento(evento.fecha_evento)}</div>
                    <div className="text-sm text-gray-500">{horaCorta(evento.hora_inicio)}</div>
                    {evento.hora_llegada && (
                      <div className="text-xs text-orange-600 mt-1">Llegada: {horaCorta(evento.hora_llegada)}</div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-200 text-xs sm:text-sm text-gray-500">
                  <span>{evento.num_personas} pax · #{evento.numero}</span>
                  <span>{evento.total_personal} personas asignadas</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Vacio Icono={Calendar} texto="No hay eventos próximos" subtexto="Los próximos 7 días están libres" />
        )}
      </Tarjeta>

      <Tarjeta titulo="Esta semana" Icono={Calendar} colorIcono="text-indigo-600" tour="semana">
        <CalendarioSemanal calendario={hoy.calendario} />
        <button
          type="button"
          onClick={() => navigate('/calendario-eventos')}
          className="w-full mt-4 flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-800 py-2"
        >
          Ver el calendario completo
          <ChevronRight className="w-4 h-4" />
        </button>
      </Tarjeta>
    </div>
  );
};

// ==================== PESTAÑA VACACIONES ====================

const FORM_SOLICITUD = { tipo: 'vacaciones', inicio: '', fin: '', motivo: '' };

const PestanaVacaciones = ({ empleadoId, vacaciones, onRecargar, soloLectura = false }) => {
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal(6000);
  const [formAbierto, setFormAbierto] = useState(false);
  const [form, setForm] = useState(FORM_SOLICITUD);
  const [enviando, setEnviando] = useState(false);
  const [errorForm, setErrorForm] = useState('');
  const [aCancelar, setACancelar] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [errorCancelar, setErrorCancelar] = useState('');

  if (!vacaciones) {
    return <Vacio Icono={Plane} texto="No se han podido cargar tus vacaciones" subtexto="Pulsa Actualizar en unos minutos." />;
  }

  const disponibles = numero(vacaciones.disponibles) ?? 0;
  const anioActual = Number(hoyMadrid().slice(0, 4));
  const solicitudes = [...(vacaciones.solicitudes || [])]
    .filter((s) => !s.fecha_inicio || Number(String(s.fecha_inicio).slice(0, 4)) >= anioActual)
    .sort((a, b) => String(b.fecha_inicio || '').localeCompare(String(a.fecha_inicio || '')));

  const dias = diasEntre(form.inicio, form.fin);
  const fechasIncompletas = !form.inicio || !form.fin;
  const finAntes = !fechasIncompletas && form.fin < form.inicio;
  const superaSaldo = form.tipo === 'vacaciones' && !fechasIncompletas && !finAntes && dias > disponibles;

  const abrirFormulario = () => {
    if (soloLectura) { mostrarAviso('error', AVISO_SIMULACION); return; }
    limpiarAviso();
    setErrorForm('');
    const hoy = hoyMadrid();
    setForm({ ...FORM_SOLICITUD, inicio: hoy, fin: hoy });
    setFormAbierto(true);
  };

  const enviarSolicitud = async () => {
    if (fechasIncompletas) { setErrorForm('Elige la fecha de inicio y la de fin.'); return; }
    if (finAntes) { setErrorForm('La fecha de fin no puede ser anterior a la de inicio.'); return; }
    setEnviando(true);
    setErrorForm('');
    try {
      await portalAPI.solicitarVacaciones({
        empleado_id: empleadoId,
        tipo: form.tipo,
        fecha_inicio: form.inicio,
        fecha_fin: form.fin,
        dias_solicitados: dias,
        motivo: form.motivo.trim() || null,
      });
      setFormAbierto(false);
      await onRecargar();
      mostrarAviso('ok', 'Solicitud enviada. Te avisaremos cuando la revisen.');
    } catch (error) {
      setErrorForm(textoErrorApi(error.response?.data?.detail, 'No se ha podido enviar la solicitud.'));
    } finally {
      setEnviando(false);
    }
  };

  const confirmarCancelar = async () => {
    if (!aCancelar) return;
    setCancelando(true);
    setErrorCancelar('');
    try {
      await portalAPI.cancelarSolicitud(aCancelar.id);
      setACancelar(null);
      await onRecargar();
      mostrarAviso('ok', 'Solicitud cancelada.');
    } catch (error) {
      setErrorCancelar(textoErrorApi(error.response?.data?.detail, 'No se ha podido cancelar la solicitud.'));
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <AvisoInline aviso={aviso} onCerrar={limpiarAviso} testId="vacaciones-aviso" />
      <Tarjeta titulo={`Vacaciones ${anioActual}`} Icono={Plane} colorIcono="text-blue-600">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <Cifra valor={formatoNumero(vacaciones.disponibles)} texto="Disponibles" clases="bg-blue-600 border-blue-600 text-white" testId="vacaciones-disponibles" />
          <Cifra valor={formatoNumero(vacaciones.devengados)} texto="Generados hasta hoy" clases="bg-green-50 border-green-200 text-green-800" />
          <Cifra valor={formatoNumero(vacaciones.usados)} texto="Disfrutados" clases="bg-orange-50 border-orange-200 text-orange-800" />
          <Cifra valor={formatoNumero(vacaciones.pendientes_aprobacion)} texto="Pendientes de aprobar" clases="bg-yellow-50 border-yellow-200 text-yellow-800" />
        </div>
        {numero(vacaciones.devengados_fin_ano) != null && numero(vacaciones.devengados_fin_ano) !== numero(vacaciones.devengados) && (
          <p className="text-xs text-gray-600 mt-3">Los días se generan día a día: al cierre del año tendrás {formatoNumero(vacaciones.devengados_fin_ano)}.</p>
        )}
        {numero(vacaciones.anuales) != null && (
          <p className="text-xs text-gray-500 mt-3">Te corresponden {formatoNumero(vacaciones.anuales)} días al año.</p>
        )}
        <button
          type="button"
          onClick={abrirFormulario}
          disabled={!empleadoId}
          aria-disabled={soloLectura || undefined}
          className={`w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl bg-duetto-primary text-white font-medium hover:opacity-90 disabled:opacity-50${soloLectura ? ' opacity-50 cursor-not-allowed' : ''}`}
          data-testid="solicitar-vacaciones"
          data-tour="vacaciones-solicitar"
        >
          <Plus className="w-5 h-5" />
          Solicitar vacaciones
        </button>
      </Tarjeta>

      <Tarjeta titulo="Mis solicitudes del año" Icono={FileText} colorIcono="text-gray-500">
        {solicitudes.length === 0 ? (
          <Vacio Icono={Calendar} texto="No tienes solicitudes este año" />
        ) : (
          <div className="space-y-3">
            {solicitudes.map((sol) => {
              const est = ESTADOS_SOLICITUD[sol.estado] || { texto: sol.estado || 'Sin estado', clases: 'bg-gray-100 text-gray-700' };
              // Día ocupado registrado por dirección (curso, trámite...): "Día ocupado · Curso"
              const tipo = sol.tipo === 'dia_ocupado'
                ? (sol.tipo_etiqueta && sol.tipo_etiqueta !== 'Día ocupado' ? `Día ocupado · ${sol.tipo_etiqueta}` : 'Día ocupado')
                : (TIPOS_SOLICITUD.find((t) => t.valor === sol.tipo)?.texto || sol.tipo);
              return (
                <div key={sol.id} className="border rounded-lg p-3" data-testid={`solicitud-${sol.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${est.clases}`}>{est.texto}</span>
                      <span className="text-sm text-gray-700">{tipo}</span>
                    </div>
                    {sol.estado === 'pendiente' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (soloLectura) { mostrarAviso('error', AVISO_SIMULACION); return; }
                          limpiarAviso(); setErrorCancelar(''); setACancelar(sol);
                        }}
                        aria-disabled={soloLectura || undefined}
                        className={`px-3 py-2.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50${soloLectura ? ' opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 mt-2">
                    Del {fechaCorta(sol.fecha_inicio)} al {fechaCorta(sol.fecha_fin)} · {sol.dias} {Number(sol.dias) === 1 ? 'día' : 'días'}
                  </div>
                  {sol.tipo === 'dia_ocupado' && (sol.hora || sol.lugar) && (
                    <div className="text-xs text-gray-500 mt-1 break-words">{[sol.hora, sol.lugar].filter(Boolean).join(' · ')}</div>
                  )}
                  {sol.motivo && <div className="text-xs text-gray-500 mt-1 break-words">Motivo: {sol.motivo}</div>}
                  {sol.estado === 'rechazada' && sol.comentarios_admin && (
                    <div className="text-xs text-red-700 bg-red-50 rounded p-2 mt-2">Respuesta: {sol.comentarios_admin}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Tarjeta>

      <DialogoConfirmar
        abierto={formAbierto}
        titulo="Solicitar vacaciones o ausencia"
        descripcion="La solicitud queda pendiente hasta que dirección la apruebe."
        textoConfirmar="Enviar solicitud"
        ocupado={enviando}
        deshabilitado={fechasIncompletas || finAntes}
        error={errorForm}
        onConfirmar={enviarSolicitud}
        onCancelar={() => { setFormAbierto(false); setErrorForm(''); }}
        testId="dialogo-solicitar-vacaciones"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sol-tipo">Tipo</label>
          <select
            id="sol-tipo"
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            {TIPOS_SOLICITUD.map((t) => <option key={t.valor} value={t.valor}>{t.texto}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sol-inicio">Desde</label>
            <input
              id="sol-inicio"
              type="date"
              value={form.inicio}
              onChange={(e) => setForm({ ...form, inicio: e.target.value, fin: form.fin && form.fin < e.target.value ? e.target.value : form.fin })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sol-fin">Hasta</label>
            <input
              id="sol-fin"
              type="date"
              value={form.fin}
              min={form.inicio || undefined}
              onChange={(e) => setForm({ ...form, fin: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
        {finAntes && <p className="text-sm text-red-600">La fecha de fin no puede ser anterior a la de inicio.</p>}
        {!fechasIncompletas && !finAntes && (
          <p className="text-sm text-gray-600">{dias} {dias === 1 ? 'día natural' : 'días naturales'}</p>
        )}
        {superaSaldo && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Pides {dias} días y te quedan {formatoNumero(disponibles)}. Puedes enviarla igualmente; dirección decidirá.
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sol-motivo">Motivo (opcional)</label>
          <textarea
            id="sol-motivo"
            rows="2"
            value={form.motivo}
            onChange={(e) => setForm({ ...form, motivo: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </DialogoConfirmar>

      <DialogoConfirmar
        abierto={!!aCancelar}
        titulo="Cancelar solicitud"
        descripcion={aCancelar ? `Se cancelará la solicitud del ${fechaCorta(aCancelar.fecha_inicio)} al ${fechaCorta(aCancelar.fecha_fin)}.` : ''}
        textoConfirmar="Cancelar solicitud"
        textoCancelar="Volver"
        peligro
        ocupado={cancelando}
        error={errorCancelar}
        onConfirmar={confirmarCancelar}
        onCancelar={() => { setACancelar(null); setErrorCancelar(''); }}
        testId="dialogo-cancelar-solicitud"
      />
    </div>
  );
};

// ==================== PESTAÑAS NÓMINAS Y DOCUMENTOS ====================

const PestanaNominas = ({ nominas, onVer }) => {
  if (!nominas) {
    return <Vacio Icono={Wallet} texto="No se han podido cargar tus nóminas" subtexto="Pulsa Actualizar en unos minutos." />;
  }
  const ordenadas = [...nominas].sort((a, b) => claveOrdenNomina(b).localeCompare(claveOrdenNomina(a)));
  return (
    <Tarjeta titulo="Mis nóminas" Icono={Wallet} colorIcono="text-green-600" tour="nominas">
      {ordenadas.length === 0 ? (
        <Vacio Icono={Wallet} texto="Todavía no tienes nóminas aquí" subtexto="Aparecen cada mes cuando la gestoría las publica." />
      ) : (
        <div className="space-y-2">
          {ordenadas.map((n) => (
            <FilaDocumento
              key={n.id}
              titulo={nombreNomina(n)}
              subtitulo={n.fecha_documento ? fechaCorta(n.fecha_documento) : ''}
              url={n.archivo_url}
              onVer={() => onVer({ titulo: nombreNomina(n), url: n.archivo_url })}
              testIdVer={`nomina-ver-${n.id}`}
            />
          ))}
        </div>
      )}
    </Tarjeta>
  );
};

const PestanaDocumentos = ({ documentos, onVer, onRecargar, resaltarFirma, soloLectura = false }) => {
  if (!documentos) {
    return <Vacio Icono={FileText} texto="No se han podido cargar tus documentos" subtexto="Pulsa Actualizar en unos minutos." />;
  }
  const grupos = (documentos.por_tipo || []).filter((g) => g.tipo !== 'nomina' && g.documentos.length > 0);
  return (
    <div className="space-y-4 sm:space-y-6">
      <Tarjeta titulo="Para firmar" Icono={FileCheck} colorIcono="text-indigo-600" testId="inicio-para-firmar" tour="para-firmar">
        <PestanaFirmas docsFirmar={documentos.para_firmar || []} onRecargar={onRecargar} resaltarId={resaltarFirma} soloLectura={soloLectura} />
      </Tarjeta>

      <Tarjeta titulo="Mis documentos" Icono={FileText} colorIcono="text-purple-600">
        {grupos.length === 0 ? (
          <Vacio texto="No tienes documentos" subtexto="Las nóminas están en su pestaña." />
        ) : (
          <div className="space-y-5">
            {grupos.map((grupo) => (
              <div key={grupo.tipo}>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{grupo.etiqueta}</h3>
                <div className="space-y-2">
                  {grupo.documentos.map((doc) => {
                    const titulo = doc.nombre || doc.descripcion || grupo.etiqueta;
                    return (
                      <FilaDocumento
                        key={doc.id}
                        titulo={titulo}
                        subtitulo={fechaCorta(doc.fecha_documento || doc.fecha_subida)}
                        url={doc.archivo_url}
                        onVer={() => onVer({ titulo, url: doc.archivo_url })}
                        testIdVer={`documento-ver-${doc.id}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Tarjeta>
    </div>
  );
};

// ==================== PESTAÑA OBLIGACIONES ====================

const ESTADOS_PARA_FIRMAR = ['Pendiente', 'Descargado', 'Rechazado'];

const FormularioIban = ({ onGuardado, onCancelar }) => {
  const [valor, setValor] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const guardar = async (e) => {
    e.preventDefault();
    const fallo = errorIban(valor);
    if (fallo) { setError(fallo); return; }
    setGuardando(true);
    setError('');
    try {
      const { data } = await portalAPI.guardarIban(limpiarIban(valor));
      setGuardando(false);
      await onGuardado(data?.iban_enmascarado);
    } catch (err) {
      setError(textoErrorApi(err.response?.data?.detail, 'No se ha podido guardar el IBAN.'));
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar} className="mt-3 space-y-2" data-testid="iban-formulario" noValidate>
      <label htmlFor="iban-empleado" className="block text-sm font-medium text-gray-800">Tu IBAN</label>
      <input
        id="iban-empleado"
        ref={inputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        value={valor}
        onChange={(e) => { setValor(formatearIban(e.target.value)); if (error) setError(''); }}
        placeholder="ES00 0000 0000 0000 0000 0000"
        aria-describedby="iban-ayuda"
        aria-invalid={error ? true : undefined}
        className={`w-full min-h-[48px] px-3 rounded-lg border font-mono text-base tracking-wide ${error ? 'border-red-400' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-duetto-primary`}
        data-testid="iban-input"
      />
      <p id="iban-ayuda" className="text-xs text-gray-500">
        Es la cuenta donde recibirás la nómina. Empieza por ES y tiene 24 caracteres.
      </p>
      {error && <p className="text-sm text-red-700" role="alert" data-testid="iban-error">{error}</p>}
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button
          type="submit"
          disabled={guardando}
          className="w-full sm:w-auto min-h-[48px] px-5 rounded-lg bg-duetto-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60"
          data-testid="iban-guardar"
        >
          {guardando ? 'Guardando...' : 'Guardar'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          disabled={guardando}
          className="w-full sm:w-auto min-h-[48px] px-5 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
          data-testid="iban-cancelar"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
};

const PestanaObligaciones = ({ obligaciones, onFirmar, onRecargar, soloLectura = false, abrirIban = false }) => {
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal(6000);
  const [formIban, setFormIban] = useState(abrirIban && !soloLectura);

  const abrirFormIban = () => {
    if (soloLectura) { mostrarAviso('error', AVISO_SIMULACION); return; }
    limpiarAviso();
    setFormIban(true);
  };

  const ibanGuardado = async (enmascarado) => {
    setFormIban(false);
    await onRecargar();
    mostrarAviso('ok', enmascarado ? `IBAN guardado: ${enmascarado}. Es donde recibirás la nómina.` : 'IBAN guardado.');
  };

  if (!obligaciones) {
    return <Vacio Icono={ListChecks} texto="No se han podido cargar tus obligaciones" subtexto="Pulsa Actualizar en unos minutos." />;
  }
  const items = obligaciones.items || [];
  const faltan = numero(obligaciones.faltan) ?? items.filter((i) => i.ok === false).length;
  return (
    <>
    <AvisoInline aviso={aviso} onCerrar={limpiarAviso} className="mb-4" testId="obligaciones-aviso" />
    <Tarjeta
      titulo="Mis obligaciones"
      Icono={ListChecks}
      colorIcono="text-duetto-primary"
      tour="obligaciones-lista"
      extra={(
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${faltan > 0 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
          {faltan > 0 ? `Faltan ${faltan}` : 'Todo al día'}
        </span>
      )}
    >
      {items.length === 0 ? (
        <Vacio Icono={ListChecks} texto="No hay puntos que revisar" />
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const asig = it.asignacion;
            // ok null: el punto no aplica a esta persona (p. ej. visita guiada no activada); sin acción
            const noAplica = it.ok === null;
            const porFirmar = !noAplica && asig && ESTADOS_PARA_FIRMAR.includes(asig.estado);
            let estado = { texto: 'Más adelante', clases: 'bg-gray-100 text-gray-600' };
            if (noAplica) estado = { texto: 'No aplica', clases: 'bg-gray-100 text-gray-500' };
            if (it.ok === true) estado = { texto: 'Hecho', clases: 'bg-green-100 text-green-800' };
            if (it.ok === false) estado = { texto: 'Pendiente', clases: 'bg-amber-100 text-amber-800' };
            return (
              <li key={it.clave} className="border rounded-lg p-3" data-testid={`obligacion-${it.clave}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900">{it.texto}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${estado.clases}`}>{estado.texto}</span>
                </div>
                {it.detalle && <p className="text-xs text-gray-600 mt-1">{it.detalle}</p>}
                {it.aviso && <p className="text-xs text-amber-700 mt-1">{it.aviso}</p>}
                {asig?.estado === 'Firmado' && (
                  <p className="text-xs text-gray-500 mt-1">Firmado, pendiente de que lo validen.</p>
                )}
                {asig?.notas_rechazo && (asig.estado === 'Pendiente' || asig.estado === 'Rechazado') && (
                  <p className="text-xs text-red-700 mt-1">Rechazado: {asig.notas_rechazo}. Vuelve a subirlo.</p>
                )}
                {porFirmar && (
                  <button
                    type="button"
                    onClick={() => onFirmar(asig.id)}
                    className="mt-2 flex items-center gap-1.5 px-3 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                    data-testid={`obligacion-firmar-${it.clave}`}
                    data-uso="mi_inicio.firmar"
                  >
                    <FileCheck className="w-3.5 h-3.5" />
                    Firmar {asig.nombre_documento ? `"${asig.nombre_documento}"` : 'el documento'}
                  </button>
                )}
                {it.accion_empleado === 'iban' && !formIban && it.ok === false && (
                  <button
                    type="button"
                    onClick={abrirFormIban}
                    aria-disabled={soloLectura || undefined}
                    className={`mt-2 w-full sm:w-auto min-h-[48px] flex items-center justify-center gap-1.5 px-4 bg-duetto-primary text-white rounded-lg text-sm font-semibold hover:opacity-90${soloLectura ? ' opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="obligacion-iban-anadir"
                    data-uso="mi_inicio.iban"
                  >
                    <Landmark className="w-4 h-4" />
                    Añadir mi IBAN
                  </button>
                )}
                {it.accion_empleado === 'iban' && !formIban && it.ok === true && (
                  <button
                    type="button"
                    onClick={abrirFormIban}
                    aria-disabled={soloLectura || undefined}
                    className={`mt-1 py-2 text-xs text-gray-500 underline hover:text-gray-700${soloLectura ? ' opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="obligacion-iban-cambiar"
                  >
                    Cambiar
                  </button>
                )}
                {it.accion_empleado === 'iban' && formIban && (
                  <FormularioIban onGuardado={ibanGuardado} onCancelar={() => setFormIban(false)} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Tarjeta>
    </>
  );
};

// ==================== PÁGINA ====================

const DashboardEmpleado = () => {
  const { user } = useAuth();
  // "Simular vista empleado" (dirección): Mi Inicio con los datos del empleado
  // elegido y en solo lectura
  const { simulando, vistaEmpleado } = useVistaEmpleado();
  const idSimulado = simulando ? vistaEmpleado.id : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [datos, setDatos] = useState(null);
  const [fichajeHoy, setFichajeHoy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accionEnCurso, setAccionEnCurso] = useState(null);
  const [visor, setVisor] = useState(null);
  // "Añadirlo" desde Hoy: abre la pestaña Obligaciones con el formulario del IBAN desplegado
  const [abrirIban, setAbrirIban] = useState(false);
  const [resaltarFirma, setResaltarFirma] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('firma') || null; } catch (e) { return null; }
  });
  const { aviso, mostrarAviso, limpiarAviso } = useAvisoTemporal(6000);
  // Pestañas en móvil: degradado a la derecha mientras quedan pestañas por ver
  const pestanasRef = useRef(null);
  const [masPestanas, setMasPestanas] = useState(false);
  const medirPestanas = useCallback(() => {
    const el = pestanasRef.current;
    if (!el) return;
    setMasPestanas(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  }, []);

  // Estado del fichaje de hoy cuando el servidor no lo trae (respaldo):
  // el registro de hoy abierto o, si no, el último cerrado
  const fichajeDeRegistros = useCallback(async (empleadoId) => {
    try {
      const { data } = await controlHorarioAPI.getRegistrosByEmpleado(empleadoId);
      const hoy = hoyMadrid();
      const deHoy = (data?.registros || []).filter((r) => r.fecha === hoy);
      const abierto = deHoy.find((r) => r.hora_entrada && !r.hora_salida);
      const registro = abierto || deHoy[0] || null;
      return {
        estado: estadoFichaje(registro),
        hora_entrada: registro?.hora_entrada || null,
        hora_salida: registro?.hora_salida || null,
        pausa_desde: null,
      };
    } catch (e) {
      return null;
    }
  }, []);

  // `silencioso`: recarga sin la pantalla de carga (tras una acción)
  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    let normal = null;
    try {
      const { data } = await portalAPI.miInicio(idSimulado ? { empleado_id_param: idSimulado } : {});
      normal = normalizar(data);
    } catch (error) {
      console.error('Error cargando Mi Inicio, se usa el resumen antiguo:', error);
      // En simulación no hay respaldo: el resumen antiguo traería los datos de dirección
      if (!idSimulado) {
        try {
          const { data } = await portalAPI.miDashboard();
          if (data && data.success !== false) normal = normalizar(data, true);
        } catch (error2) {
          console.error('Error cargando el resumen antiguo:', error2);
        }
      }
    }
    try {
      if (!normal) return false;
      // Respaldo de lo que falte en la respuesta (en simulación no: misDocumentos
      // devolvería los de dirección)
      if (normal.empleadoId && !normal.documentos && !idSimulado) {
        try {
          const res = await gestionDocumentalAPI.misDocumentos();
          normal.documentos = { por_tipo: [], para_firmar: res.data || [] };
        } catch (e) {
          console.error('Error cargando documentos para firmar:', e);
        }
      }
      let fichaje = normal.hoy.fichaje;
      if (!fichaje && normal.empleadoId && !normal.sinFicha) {
        fichaje = await fichajeDeRegistros(normal.empleadoId);
      }
      setDatos(normal);
      setFichajeHoy(fichaje);
      return true;
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [fichajeDeRegistros, idSimulado]);

  useEffect(() => {
    if (user) cargar();
  }, [user, cargar]);

  const recargar = useCallback(async () => {
    const ok = await cargar(true);
    if (!ok) mostrarAviso('error', 'No se pudieron recargar los datos. Pulsa Actualizar.');
  }, [cargar, mostrarAviso]);

  const sinFicha = datos?.sinFicha === true;
  const pestanasVisibles = useMemo(
    () => (sinFicha ? PESTANAS.filter((p) => p.clave === 'hoy') : PESTANAS),
    [sinFicha],
  );
  const tabPedida = searchParams.get('tab');
  const tabActiva = !sinFicha && CLAVES_PESTANAS.includes(tabPedida) ? tabPedida : 'hoy';

  // La pestaña activa a la vista (en móvil las pestañas se desplazan en horizontal)
  useEffect(() => {
    const el = pestanasRef.current?.querySelector(`[data-testid="inicio-tab-${tabActiva}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    const t = setTimeout(medirPestanas, 350);
    return () => clearTimeout(t);
  }, [tabActiva, medirPestanas, datos]);

  useEffect(() => {
    window.addEventListener('resize', medirPestanas);
    return () => window.removeEventListener('resize', medirPestanas);
  }, [medirPestanas]);

  // Guía de bienvenida (interactiva, por las pantallas reales): se abre sola
  // una vez por carga si la ficha la tiene pendiente, no está pospuesta (en el
  // servidor, `tour_pospuesto`, ni en esta sesión) ni en marcha. Pospuesta, la
  // pestaña Hoy enseña un aviso con "Ver la guía" mientras siga pendiente.
  // Nunca en la simulación (allí la abre "Ver su guía").
  const {
    estado: estadoGuia, guiaActiva, hayGuiaPospuesta, iniciarGuia, retomarGuia,
  } = useGuia();
  // Una novedad abierta (NovedadesPendientes) no cuenta como guía lanzada: al cerrarla sale la de bienvenida
  const novedadAbierta = !!estadoGuia?.novedad;
  const guiaLanzada = useRef(false);
  const tourPendiente = datos?.tour_pendiente === true && !sinFicha && !simulando;
  const tourPospuesto = tourPendiente && datos?.tour_pospuesto === true;
  const pasosGuia = useCallback(
    () => pasosBienvenida(user?.permisos_modulos || [], user?.rol, window.innerWidth < 768),
    [user],
  );
  useEffect(() => {
    // Con la guía en marcha en esta carga, al terminarla no se vuelve a abrir
    if (guiaActiva && !novedadAbierta) guiaLanzada.current = true;
    if (guiaLanzada.current || guiaActiva || !tourPendiente || tourPospuesto || hayGuiaPospuesta) return;
    guiaLanzada.current = true;
    iniciarGuia({ pasos: pasosGuia() });
  }, [tourPendiente, tourPospuesto, guiaActiva, novedadAbierta, hayGuiaPospuesta, iniciarGuia, pasosGuia]);
  // Retoma donde se dejó si este navegador lo recuerda; si no, desde el principio
  const verGuia = useCallback(() => retomarGuia(pasosGuia()), [retomarGuia, pasosGuia]);
  // Aviso de la pestaña Hoy y botón de la cabecera (en las demás pestañas)
  const mostrarVerGuia = tourPendiente && !guiaActiva && (tourPospuesto || hayGuiaPospuesta);

  // Al terminar la guía, Mi Inicio recarga (deja de estar pendiente)
  useEffect(() => {
    const alTerminar = () => cargar(true);
    window.addEventListener(EVENTO_GUIA_TERMINADA, alTerminar);
    return () => window.removeEventListener(EVENTO_GUIA_TERMINADA, alTerminar);
  }, [cargar]);

  const cambiarTab = useCallback((clave) => {
    if (clave !== 'documentos') setResaltarFirma(null);
    if (clave !== 'obligaciones') setAbrirIban(false);
    const nuevos = new URLSearchParams(searchParams);
    if (clave === 'hoy') nuevos.delete('tab');
    else nuevos.set('tab', clave);
    setSearchParams(nuevos, { replace: true });
  }, [searchParams, setSearchParams]);

  const irAAnadirIban = useCallback(() => {
    cambiarTab('obligaciones');
    setAbrirIban(true);
  }, [cambiarTab]);

  const irAFirmar = useCallback((asignacionId) => {
    setResaltarFirma(asignacionId);
    const nuevos = new URLSearchParams(searchParams);
    nuevos.set('tab', 'documentos');
    setSearchParams(nuevos, { replace: true });
  }, [searchParams, setSearchParams]);

  // Marca la tarea en el servidor y recarga; si falla, aviso visible
  const accionTarea = async (notificacionId, accion) => {
    if (simulando) {
      mostrarAviso('error', AVISO_SIMULACION);
      return;
    }
    const textoFallo = accion === 'leer' ? 'No se pudo marcar como leído' : 'No se pudo marcar como realizado';
    setAccionEnCurso(`${notificacionId}-${accion}`);
    try {
      await api.put(`/notificaciones/${notificacionId}/${accion}`);
      limpiarAviso();
      const ok = await cargar(true);
      if (!ok) mostrarAviso('error', 'La tarea se ha marcado, pero no se pudo recargar la lista. Pulsa Actualizar.');
    } catch (error) {
      const detalle = textoErrorApi(error.response?.data?.detail, error.message || 'Error desconocido');
      mostrarAviso('error', `${textoFallo}: ${detalle}`);
    } finally {
      setAccionEnCurso(null);
    }
  };

  if (loading) {
    return (
      <div className="py-16 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-duetto-primary mx-auto" />
          <p className="mt-4 text-gray-600">Cargando Mi Inicio...</p>
        </div>
      </div>
    );
  }

  const nombre = (datos?.nombre || (simulando ? vistaEmpleado?.nombre : user?.nombre) || 'Empleado').split(' ')[0];
  const rol = rolLegible(datos?.rol || (simulando ? vistaEmpleado?.rol : user?.rol) || '');
  const pendientesFirma = (datos?.documentos?.para_firmar || []).filter((d) => ESTADOS_PARA_FIRMAR.includes(d.estado)).length;
  const faltanObligaciones = numero(datos?.obligaciones?.faltan) || 0;
  const contadores = { documentos: pendientesFirma, obligaciones: faltanObligaciones };

  return (
    <div className="bg-gray-50">
      {/* Cabecera: en móvil a todo el ancho (anula el relleno del Layout) */}
      <div className="bg-gradient-to-r from-duetto-primary to-emerald-800 -mx-4 -mt-4 sm:mx-0 sm:mt-0">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
          <div className="flex items-start justify-between gap-3">
            <div className="text-white min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold truncate">Hola, {nombre}</h1>
              <p className="text-white/80 mt-1 text-sm sm:text-base capitalize">
                {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              {rol && (
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm">{rol}</span>
              )}
              {simulando && (
                <p className="mt-2 text-xs sm:text-sm font-medium text-amber-200" data-testid="dashboard-empleado-simulacion">
                  Vista de simulación: las acciones están desactivadas
                </p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
              {mostrarVerGuia && tabActiva !== 'hoy' && (
                <button
                  type="button"
                  onClick={verGuia}
                  className="flex items-center gap-2 px-3 py-2 bg-white text-duetto-primary hover:bg-emerald-50 rounded-lg text-sm font-semibold"
                  data-testid="guia-retomar"
                >
                  <Compass className="w-4 h-4" />
                  Retomar la guía
                </button>
              )}
              <button
                type="button"
                onClick={() => cargar()}
                className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white text-sm flex-shrink-0"
                title="Actualizar"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Actualizar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-0 sm:px-6 py-4 sm:py-6">
        <AvisoInline aviso={aviso} onCerrar={limpiarAviso} className="mb-4" testId="dashboard-empleado-aviso" />
        {datos?.parcial && (
          <div className="mb-4 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Algunas secciones no se han podido cargar. Pulsa Actualizar en unos minutos.
          </div>
        )}

        {datos ? (
          <Tabs value={tabActiva} onValueChange={cambiarTab}>
            <div className="relative -mx-4 sm:mx-0">
            <div
              ref={pestanasRef}
              onScroll={medirPestanas}
              className="px-4 sm:px-0 overflow-x-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <TabsList className="h-auto w-max sm:w-full justify-start bg-white border border-gray-200 p-1 gap-1">
                {pestanasVisibles.map(({ clave, texto, corto, Icono }) => (
                  <TabsTrigger
                    key={clave}
                    value={clave}
                    className="flex items-center gap-1.5 px-3 py-2 data-[state=active]:bg-duetto-primary data-[state=active]:text-white"
                    data-testid={`inicio-tab-${clave}`}
                    data-tour={`tab-${clave}`}
                    data-uso="mi_inicio.pestana"
                    data-uso-detalle={clave}
                  >
                    <Icono className="w-4 h-4" />
                    <span className="sm:hidden">{corto || texto}</span>
                    <span className="hidden sm:inline">{texto}</span>
                    {contadores[clave] > 0 && (
                      <span className="ml-0.5 min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none">
                        {contadores[clave]}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            {masPestanas && (
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-gray-50 to-transparent sm:hidden" aria-hidden="true" />
            )}
            </div>

            <TabsContent value="hoy" className="mt-4">
              {mostrarVerGuia && (
                <div
                  className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3"
                  data-testid="guia-aviso-pendiente"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0 text-sm text-emerald-900">
                    <Compass className="w-5 h-5 text-duetto-primary flex-shrink-0" />
                    <span>Tienes la guía de bienvenida pendiente</span>
                  </div>
                  <button
                    type="button"
                    onClick={verGuia}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-duetto-primary text-white hover:opacity-90 rounded-lg text-sm font-semibold"
                    data-uso="mi_inicio.ver_guia"
                    data-testid="guia-ver"
                  >
                    Ver la guía
                  </button>
                </div>
              )}
              <PestanaHoy
                datos={datos}
                fichajeHoy={fichajeHoy}
                onRecargar={recargar}
                onTarea={accionTarea}
                accionEnCurso={accionEnCurso}
                soloLectura={simulando}
                onAnadirIban={irAAnadirIban}
              />
            </TabsContent>

            {!sinFicha && (
              <>
                <TabsContent value="vacaciones" className="mt-4">
                  <PestanaVacaciones empleadoId={datos.empleadoId} vacaciones={datos.vacaciones} onRecargar={recargar} soloLectura={simulando} />
                </TabsContent>
                <TabsContent value="nominas" className="mt-4">
                  <PestanaNominas nominas={datos.nominas} onVer={setVisor} />
                </TabsContent>
                <TabsContent value="documentos" className="mt-4">
                  <PestanaDocumentos
                    documentos={datos.documentos}
                    onVer={setVisor}
                    onRecargar={recargar}
                    resaltarFirma={resaltarFirma}
                    soloLectura={simulando}
                  />
                </TabsContent>
                <TabsContent value="registro" className="mt-4">
                  <Tarjeta titulo="Registro horario" Icono={Calendar} colorIcono="text-blue-600">
                    {datos.empleadoId ? (
                      <RegistroHorarioEmpleado empleadoId={datos.empleadoId} soloLectura />
                    ) : (
                      <Vacio Icono={Calendar} texto="No se ha podido identificar tu ficha" />
                    )}
                  </Tarjeta>
                </TabsContent>
                <TabsContent value="obligaciones" className="mt-4">
                  <PestanaObligaciones
                    key={abrirIban ? 'abrir-iban' : 'obligaciones'}
                    obligaciones={datos.obligaciones}
                    onFirmar={irAFirmar}
                    onRecargar={recargar}
                    soloLectura={simulando}
                    abrirIban={abrirIban}
                  />
                </TabsContent>
              </>
            )}
          </Tabs>
        ) : (
          <div className="text-center py-16">
            <AlertCircle className="w-14 h-14 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">No se pudieron cargar los datos</p>
            <button
              type="button"
              onClick={() => cargar()}
              className="mt-4 px-6 py-2 bg-duetto-primary text-white rounded-lg hover:opacity-90"
            >
              Reintentar
            </button>
          </div>
        )}
      </div>

      <VisorDocumento
        abierto={!!visor}
        onCerrar={() => setVisor(null)}
        titulo={visor?.titulo}
        url={visor?.url || undefined}
      />
    </div>
  );
};

export default DashboardEmpleado;
